import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import UserChip from './UserChip';
import { Avatar } from '@material-ui/core';
import { Typography, colors } from 'kangarootime-ui';
import { difference } from 'lodash';
import './MultiItemSelect.css';

const MultiItemSelect = ({
  items,
  filteredItems,
  onItemSelect,
  onFilter,
  label,
  renderSelectedItems,
  renderMenuItem,
  disableSelection,
  dropdownItemKeyExtractor,
  ...props
}) => {
  const multiItemContainerRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);

  const addItemToSelection = useCallback(item => {
    if (!disableSelection) {
      const copy = [...selectedItems, item];
      setSelectedItems(copy);
    }

    if (filterValue) {
      setFilterValue('');
    }
  });

  const removeItemFromSelection = useCallback(user => {
    const filtered = selectedItems.filter(u => u.id !== user.id);
    setSelectedItems(filtered);
  });

  const handleModalClicks = useCallback(event => {
    if (showDropdown) {
      // target is not in the list dropdown or the container component
      if (multiItemContainerRef.current && multiItemContainerRef.current.contains(event.target)) {
        return;
      }

      // click is on a list item
      if (event.target.className && typeof event.target.className === 'string') {
        // only try if class name is a string
        if (event.target.className.match(/multi-item/)) {
          return;
        }
      }

      setShowDropdown(false);
    }
  });

  // everytime the selected users changes and the onUserSelect prop is available, bubble those changes up
  useEffect(() => {
    if (onItemSelect) {
      onItemSelect(selectedItems);
    }
  }, [selectedItems]);

  useEffect(() => {
    if (onFilter) {
      onFilter(filterValue);
    }
  }, [filterValue]);

  // listen to all clicks on the webpage
  useEffect(() => {
    document.addEventListener('click', handleModalClicks);

    return () => {
      document.removeEventListener('click', handleModalClicks);
    };
  }, [showDropdown]);

  // reset selected users when the provided items change
  useEffect(() => {
    setSelectedItems([]);
    setFilterValue('');
  }, [items]);

  return (
    <React.Fragment>
      <Typography style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>{label}</Typography>
      <div className='multi-item-wrapper' ref={multiItemContainerRef}>
        <div className='multi-item-header'>
          { selectedItems.map(item => {
            const [firstName, lastName] = item.name.split(' ');
            const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;

            return (
              <UserChip
                key={item.id}
                label={item.name}
                initials={initials}
                onDelete={() => removeItemFromSelection(item)}
              />
            );
          })}
          <input
            className='multi-item-input'
            type='text'
            value={filterValue}
            onChange={event => setFilterValue(event.target.value)}
            onFocus={() => setShowDropdown(true)}
          />
        </div>
        { showDropdown &&
          <ul className='multi-item-list'>
            { difference(filteredItems, selectedItems).map(item => {
              const [firstName, lastName] = item.name.split(' ');
              const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;

              return (
                <li
                  key={`multi-item-item-${dropdownItemKeyExtractor(item)}`}
                  className='multi-item-list-item'
                  name='multi-item-list-item'
                  onClick={() => addItemToSelection(item)}
                >
                  <Avatar style={{ height: 24, width: 24, fontSize: '0.625rem', backgroundColor: '#F1F4F8', color: colors.brand_4 }}>{initials}</Avatar>
                  <span className='multi-item-label'>{item.name}</span>
                </li>
              );
            })}
          </ul>
        }
      </div>
    </React.Fragment>
  );
};

MultiItemSelect.propTypes = {
  label: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    avatar: PropTypes.string
  })),
  filteredItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    avatar: PropTypes.string
  })),
  onItemSelect: PropTypes.func,
  onFilter: PropTypes.func,
  renderSelectedItems: PropTypes.func,
  renderMenuItem: PropTypes.func,
  disableSelection: PropTypes.bool,
  dropdownItemKeyExtractor: PropTypes.func
};

MultiItemSelect.defaultProps = {
  disableSelection: false,
  dropdownItemKeyExtractor: item => `${item.id}`
};

export default MultiItemSelect;
